import { useEffect, useState } from "react";
import { meta, sections } from "@/content/recommendation";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const Block = ({ block }) => {
  if (block.lead) return <p className="doc-lead">{block.lead}</p>;
  if (block.p) return <p className="doc-p">{block.p}</p>;
  if (block.h) return <h3 className="doc-h3">{block.h}</h3>;
  if (block.list)
    return (
      <ul className="doc-list">
        {block.list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  if (block.numlist)
    return (
      <ol className="doc-numlist">
        {block.numlist.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ol>
    );
  if (block.table) {
    const t = block.table;
    return (
      <div className="doc-table-wrap">
        <table className="doc-table">
          <thead>
            <tr>
              {t.head.map((h, i) => (
                <th key={i} className={t.highlight === i ? "col-highlight" : ""}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {t.rows.map((row, ri) => (
              <tr key={ri} className={row[0] === "TOTAL" ? "row-total" : ""}>
                {row.map((cell, ci) => (
                  <td key={ci} className={t.highlight === ci ? "col-highlight" : ""}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.callout) {
    const c = block.callout;
    const Icon = c.tone === "green" ? CheckCircle2 : AlertTriangle;
    return (
      <aside className={`doc-callout ${c.tone === "green" ? "callout-green" : "callout-amber"}`}>
        <div className="callout-label">
          <Icon size={16} strokeWidth={2.2} aria-hidden="true" />
          <span>{c.label}</span>
        </div>
        <p>{c.text}</p>
      </aside>
    );
  }
  return null;
};

const Toc = ({ active }) => (
  <nav className="doc-toc" aria-label="Table of contents" data-testid="doc-toc">
    <p className="toc-heading">Contents</p>
    <ol>
      {sections.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            data-testid={`toc-link-${s.id}`}
            className={active === s.id ? "toc-active" : ""}
          >
            <span className="toc-num">{s.num}</span>
            {s.title}
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

export default function Doc() {
  const [active, setActive] = useState(sections[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="doc-page" data-testid="architecture-recommendation">
      <div className="doc-body">
        <Toc active={active} />
        <main className="doc-main">
          <div className="doc-masthead">
            <p className="masthead-kicker">Phase 1 — Discovery & Architecture Decision</p>
            <h1 className="masthead-title" data-testid="doc-title">
              {meta.title}
            </h1>
            <p className="masthead-sub">{meta.subtitle}</p>
            <div className="masthead-meta">
              <span>{meta.date}</span>
              <span className="meta-dot" aria-hidden="true" />
              <span data-testid="doc-status">{meta.status}</span>
            </div>
          </div>

          {sections.map((s) => (
            <section key={s.id} id={s.id} className="doc-section" data-testid={`section-${s.id}`}>
              <div className="section-head">
                <span className="section-num" aria-hidden="true">
                  {s.num}
                </span>
                <h2 className="section-title">{s.title}</h2>
              </div>
              {(Array.isArray(s.blocks) ? s.blocks : [s.blocks]).map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </section>
          ))}

          <footer className="doc-footer">
            <p>
              Prepared as the Phase 1 deliverable for the HARC website engagement. No design or
              development begins until this recommendation is approved.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}
